import React, { ChangeEvent, KeyboardEvent, useState } from "react";
import { useFormikContext } from "formik";
import { twMerge } from "tailwind-merge";

import { Icon } from "@ui/icons/icon";
import { ICON_COLLECTION } from "@ui/icons/icon-list";

import { CreateFundFormType } from "@/pages/create-fund/types";
import { Token } from "@/pages/create-fund/components/select-tokens/select-tokens";

import styles from "../../create-fund-form/create-fund-form.module.scss";

const distributeEvenly = (count: number): number[] => {
  if (count === 0) return [];
  const base = Math.floor(100 / count);
  const factors = Array(count).fill(base);
  let rest = 100 - base * count;
  for (let i = 0; rest > 0; i++, rest--) {
    factors[i] += 1;
  }
  return factors;
};

interface SelectedTokensRowProps {
  token: Token;
}

const isDistributionChanged = (arr: Token[]) => {
  if (arr.length === 0) return false;

  const initDistribution = arr[0]?.distribution;
  return arr.every((token) => token.distribution === initDistribution);
};

export const SelectedTokensRow: React.FC<SelectedTokensRowProps> = ({
  token,
}) => {
  const { values, setFieldValue } = useFormikContext<CreateFundFormType>();
  const [decimalError, setDecimalError] = useState(false);

  const handleDelete = () => {
    const filteredIds = values.selectedTokenIds.filter(
      (id) => id !== token.policy_id,
    );

    let newTokens = Object.fromEntries(
      filteredIds.map((id) => [id, values.selectedTokens[id]]),
    );

    const shouldRecalculateDistribution = isDistributionChanged(
      filteredIds.map((id) => values.selectedTokens[id]),
    );

    if (shouldRecalculateDistribution) {
      const factors = distributeEvenly(filteredIds.length);
      newTokens = Object.fromEntries(
        filteredIds.map((id, i) => [
          id,
          { ...values.selectedTokens[id], distribution: factors[i] },
        ]),
      );
    }

    setFieldValue("selectedTokenIds", filteredIds);
    setFieldValue("selectedTokens", newTokens);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "." || event.key === ",") {
      event.preventDefault();
      setDecimalError(true);
      setTimeout(() => setDecimalError(false), 2000);
    }
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    if (raw === "") {
      setFieldValue(`selectedTokens.${token.policy_id}.distribution`, "");
      return;
    }
    const value = parseInt(raw, 10);
    setFieldValue(
      `selectedTokens.${token.policy_id}.distribution`,
      isNaN(value) ? 0 : value,
    );
  };

  const imageLink = token.token_image?.startsWith("ipfs")
    ? token.token_image.replace("ipfs://", "https://ipfs.io/ipfs/")
    : token.token_image;

  return (
    <div className="flex items-center justify-between w-full py-3 border-b border-gray-100 dark:border-gray-800">
      <div className="flex items-center gap-2">
        <img src={imageLink} alt="" className="h-12" />
        <h6 className="font-sf-pro text-lg md:text-xl text-gray-800 dark:text-white font-semibold">
          {token.token_symbol}
        </h6>
        {/*{token.verified ? (*/}
        {/*  <Icon icon={ICON_COLLECTION.circleCheck} iconColor="#7C3AED" />*/}
        {/*) : null}*/}
      </div>
      <div className="flex items-center gap-3">
        <div className="flex flex-col items-end">
          <div className={styles["input-with-addon"]}>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              name="token-distribution"
              value={token.distribution}
              onKeyDown={handleKeyDown}
              onChange={handleOnChange}
              className="px-3 pr-8 py-2 border border-gray-200 dark:border-gray-800 dark:bg-gray-800 rounded-lg font-sf-pro text-gray-800 dark:text-white w-[80px]"
            />
            <span className={twMerge(styles["input-addon"], "dark:text-white")}>
              %
            </span>
          </div>
          {decimalError && (
            <p className="text-xs text-red-400 mt-1">Must be a whole number</p>
          )}
        </div>
        <button
          onClick={handleDelete}
          className="flex items-center justify-center"
        >
          <Icon
            icon={ICON_COLLECTION.xCircle}
            className="height-24"
            iconColor="#6B7280"
            hoverColor="#EF4444"
          />
        </button>
      </div>
    </div>
  );
};
